import { CheckCircle, Check } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/Button";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";

export function Planos() {
  const plans = [
    {
      name: "Básico",
      category: "Essencial",
      price: "R$ 229,90",
      period: "/mês",
      description: "Para empresas que querem implantar seu canal de denúncias com segurança, rastreabilidade e conformidade legal.",
      features: [
        "Canal personalizável com a identidade da sua marca",
        "Conformidade com a NR-1",
        "Suporte por e-mail (48h)",
        "Dashboard (Simplificado)",
        "SLA de resposta (Padrão)",
        "1 acesso da equipe ao painel administrativo"
      ],
      popular: false,
      buttonVariant: "outline" as const
    },
    {
      name: "Premium",
      category: "Profissional",
      price: "R$ 449,90",
      period: "/mês",
      description: "Para empresas que precisam operar, monitorar e gerir ativamente seu canal de denúncias com controle total e conformidade avançada.",
      features: [
        "Acesso ao Portal Ethics de Treinamentos",
        "Opção de 3 treinamentos de Compliance no portal",
        "Certificado dos Treinamentos",
        "Dashboards (Avançados)",
        "Suporte por e-mail e chat (24H)",
        "SLA de resposta (Prioritário)",
        "2 acessos da equipe ao painel administrativo"
      ],
      popular: true,
      buttonVariant: "primary" as const,
      extraFeatures: "Tudo do Básico, mais:"
    },
    {
      name: "Personalizado",
      category: "Enterprise",
      price: "Sob Consulta",
      period: "",
      description: "Para empresas que precisam de uma solução de canal de denúncias moldada à sua estrutura, volume e necessidades operacionais.",
      features: [
        "Treinamento da equipe",
        "Opção de 10 treinamentos da plataforma",
        "Canal de suporte exclusivo com responsável dedicado",
        "Suporte prioritário e exclusivo (Gerente dedicado)",
        "5+ acessos da equipe ao painel administrativo"
      ],
      popular: false,
      buttonVariant: "outline" as const,
      extraFeatures: "Tudo do Premium, mais:"
    }
  ];

  const comparisonRows = [
    { feature: "Canal de Denúncias Completo", basico: true, premium: true, personalizado: true },
    { feature: "Canal Personalizável", basico: true, premium: true, personalizado: true },
    { feature: "Conformidade NR-1", basico: true, premium: true, personalizado: true },
    { feature: "Portal de Treinamentos", basico: false, premium: true, personalizado: true },
    { feature: "Treinamentos Disponíveis", basico: "-", premium: "3 treinamentos", personalizado: "10 treinamentos" },
    { feature: "Certificados", basico: false, premium: true, personalizado: true },
    { feature: "Dashboard", basico: "Simplificado", premium: "Avançados", personalizado: "Avançados" },
    { feature: "Suporte", basico: "E-mail (48h)", premium: "E-mail e Chat (24H)", personalizado: "Gerente Dedicado" },
    { feature: "SLA de Resposta", basico: "Padrão", premium: "Prioritário", personalizado: "Exclusivo" },
    { feature: "Acessos ao Painel", basico: "1 usuário", premium: "2 usuários", personalizado: "5+ usuários" }
  ];

  return (
    <>
      <Helmet>
        <title>Planos e Preços - Portal Ethics</title>
        <meta name="description" content="Escolha o plano ideal para fortalecer a cultura ética da sua organização." />
      </Helmet>

      <div className="min-h-screen bg-background-light">
        <Header />
        
        <main className="pt-32 pb-20">
          <div className="max-w-7xl mx-auto px-6">
            {/* Hero */}
            <div className="max-w-[1200px] w-full flex flex-col items-center text-center gap-6 mb-16 mx-auto">
              <h1 className="text-neutral-900 text-5xl font-black leading-tight tracking-tight">
                Planos e Preços
              </h1>
              <p className="text-neutral-600 text-lg max-w-2xl">
                Transparência e conformidade para todos os tamanhos de empresa. Escolha o plano ideal para fortalecer a cultura ética da sua organização.
              </p>
              
              {/* Toggle Billing */}
              <div className="flex items-center gap-4 bg-neutral-200/50 p-1.5 rounded-xl mt-4">
                <button className="px-6 py-2 rounded-lg bg-white shadow-sm text-sm font-bold text-primary-blue">
                  Mensal
                </button>
                <button className="px-6 py-2 rounded-lg text-sm font-bold text-neutral-500 hover:text-neutral-700 flex gap-2 items-center">
                  Anual 
                  <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full">-20%</span>
                </button>
              </div>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-[1140px] w-full px-4 mb-24 mx-auto">
              {plans.map((plan, index) => (
                <div 
                  key={index}
                  className={`relative flex flex-col gap-8 rounded-2xl border p-8 transition-shadow hover:shadow-md ${
                    plan.popular 
                      ? "border-2 border-primary-blue bg-white shadow-xl transform scale-105 z-10" 
                      : "border-neutral-200 bg-white shadow-sm"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-blue text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full">
                      Mais Popular
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2">
                    <span className={`text-sm font-bold uppercase tracking-widest ${plan.popular ? "text-primary-blue" : "text-neutral-500"}`}>
                      {plan.category}
                    </span>
                    <h2 className="text-neutral-900 text-2xl font-bold">{plan.name}</h2>
                    <div className="flex items-baseline gap-1 mt-4">
                      <span className="text-neutral-900 text-4xl font-black tracking-tight">{plan.price}</span>
                      <span className="text-neutral-500 text-base">{plan.period}</span>
                    </div>
                    <p className="text-neutral-600 text-sm mt-2">{plan.description}</p>
                  </div>

                  {plan.name === "Personalizado" ? (
                    <Button 
                      variant={plan.buttonVariant} 
                      className="w-full"
                    >
                      Contatar Vendas
                    </Button>
                  ) : (
                    <Link to="/cadastro">
                      <Button 
                        variant={plan.buttonVariant} 
                        className="w-full"
                      >
                        {plan.popular ? "Assinar Agora" : "Começar Agora"}
                      </Button>
                    </Link>
                  )}

                  <div className="flex flex-col gap-4">
                    <p className="text-neutral-900 text-sm font-bold">
                      {plan.extraFeatures || "O que está incluído:"}
                    </p>
                    <ul className="space-y-3">
                      {plan.features.map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center gap-3 text-sm text-neutral-600">
                          <CheckCircle className={`w-5 h-5 ${plan.popular ? "text-primary-blue fill-primary-blue/20" : "text-primary-blue"}`} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Comparison Table */}
            <div className="max-w-[1140px] w-full px-4 mt-8 mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-neutral-900 text-3xl font-bold tracking-tight mb-4">Comparativo Completo</h2>
                <p className="text-neutral-500">Compare detalhadamente todos os recursos disponíveis em cada plano.</p>
              </div>
              
              <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white shadow-sm">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-neutral-50">
                      <th className="p-6 text-sm font-bold text-neutral-900 border-b border-neutral-200">Recurso</th>
                      <th className="p-6 text-sm font-bold text-neutral-900 border-b border-neutral-200">Básico</th>
                      <th className="p-6 text-sm font-bold text-primary-blue border-b border-neutral-200">Premium</th>
                      <th className="p-6 text-sm font-bold text-neutral-900 border-b border-neutral-200">Personalizado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-100">
                    {comparisonRows.map((row, index) => (
                      <tr key={index}>
                        <td className="p-6 text-sm font-medium text-neutral-700">{row.feature}</td>
                        <td className="p-6">
                          {typeof row.basico === "boolean" ? (
                            row.basico ? <Check className="w-5 h-5 text-green-500" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className="text-xs text-neutral-500">{row.basico}</span>
                          )}
                        </td>
                        <td className="p-6">
                          {typeof row.premium === "boolean" ? (
                            row.premium ? <Check className="w-5 h-5 text-green-500 font-bold" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className={`text-xs ${row.premium.includes("Avançados") ? "text-primary-blue font-bold" : "text-primary-blue"}`}>{row.premium}</span>
                          )}
                        </td>
                        <td className="p-6">
                          {typeof row.personalizado === "boolean" ? (
                            row.personalizado ? <Check className="w-5 h-5 text-green-500 font-bold" /> : <span className="text-neutral-300">-</span>
                          ) : (
                            <span className="text-xs text-neutral-900 font-bold">{row.personalizado}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Trust Badges */}
            <div className="mt-24 mb-12 flex flex-col items-center gap-10">
              <p className="text-neutral-500 text-sm font-bold uppercase tracking-widest">Confiado por grandes empresas</p>
              <div className="flex flex-wrap justify-center gap-12 opacity-50">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-8 w-32 bg-neutral-400 rounded flex items-center justify-center text-white text-xs font-bold">
                    LOGO {i}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}
